/**
 * One place that answers "may this user see this route?".
 *
 * <p>Three callers used to answer it three ways, and they disagreed. The route
 * guard read `meta.requiredRoles`; `Login.vue` hardcoded `school.dashboard` as
 * everybody's landing; `SchoolLayout` hand-listed one special case
 * (canteen-admin-only) to hide the rest of the sidebar. A staff member who was
 * neither a school admin nor a canteen admin — a teacher, a head of department —
 * authenticated successfully and was then bounced from `/school` straight back
 * to the sign-in screen with no message, which the SP5A walkthrough found while
 * trying to approve a paper as an HOD.
 *
 * <p>Everything here is pure and takes what it needs, so it can be tested
 * without a router or a store.
 */
import type { RouteLocationNormalized, RouteRecordNormalized, Router } from 'vue-router'
import type { UserRole } from '@/stores/auth'

/** Roles and permissions, as the guard sees them. */
export interface Principal {
  roles: readonly UserRole[]
  permissions: readonly string[]
  /** Super admins pass every ROLE check; they also impersonate into schools. */
  isSuperAdmin: boolean
  /**
   * The SCHOOL's module entitlement — what it bought, not what this user may
   * do. Super admin does NOT bypass it; see `passesRecord`.
   *
   * `undefined` means NOT YET KNOWN — a session whose token predates the
   * entitlement rollout. That is deliberately distinct from `[]`, which means
   * "known, and this school has nothing". See `passesRecord`.
   */
  capabilities?: readonly string[]
  /**
   * The SCHOOL's modules. Needed alongside capabilities because modules
   * overlap: Transport also grants `finance.billing` and
   * `attendance.register`, so capabilities alone make a transport-only school
   * look like it bought part of Finance and Academics. Same `undefined` rule
   * as `capabilities`.
   */
  modules?: readonly string[]
}

type Gated = {
  meta: {
    requiredRoles?: UserRole[]
    requiredPermissions?: string[]
    requiredCapabilities?: string[]
    /**
     * The school must hold AT LEAST ONE of these modules. For menus that
     * belong to a module whose capabilities another module also grants: the
     * Finance pages stay out of a transport-only school's sidebar even though
     * Transport's billing keeps working underneath.
     */
    anyOfModules?: string[]
  }
}

/**
 * Every role that appears on at least one route under `/school`.
 *
 * <p>The parent `/school` record is a section gate, not an authorisation
 * decision: each child carries the roles that may actually open it, and the
 * server refuses anything the token does not cover. Listing only the two admin
 * roles here made every other role's children unreachable, since the parent is
 * matched first.
 */
export const SCHOOL_SECTION_ROLES: UserRole[] = [
  'school_admin',
  'canteen_admin', 'canteen_manager', 'canteen_cashier',
  'principal', 'deputy_principal', 'head_of_department',
  'teacher', 'homeroom_teacher',
  'transport_manager',
]

/** True when the principal satisfies one record's own role/permission meta. */
export function passesRecord(p: Principal, record: Gated): boolean {
  // Entitlement is checked BEFORE the super-admin bypass, on purpose. It is a
  // property of the school being viewed, not of the viewer: a super admin
  // impersonating a transport-only school must see what that school sees, or
  // every support session reports surfaces the school cannot actually open.
  //
  // An UNDEFINED capability set is "not yet known", not "entitled to nothing",
  // and opens the route. During a rollout an access token minted by the
  // previous backend carries no capabilities, and treating that as a denial
  // would blank the entire app for everyone signed in at deploy time. The
  // server enforces the same gate and still answers 403, so nothing is
  // actually reachable that shouldn't be — hiding nav is a courtesy, the 403
  // is the control. An explicit [] is a real answer and does deny.
  const caps = record.meta.requiredCapabilities
  const held = p.capabilities
  if (held !== undefined && caps && caps.length > 0 && !caps.every((c) => held.includes(c))) {
    return false
  }

  const wanted = record.meta.anyOfModules
  const owned = p.modules
  if (owned !== undefined && wanted && wanted.length > 0 && !wanted.some((m) => owned.includes(m))) {
    return false
  }

  if (p.isSuperAdmin) return true

  const roles = record.meta.requiredRoles
  if (roles && roles.length > 0 && !roles.some((r) => p.roles.includes(r))) return false

  const perms = record.meta.requiredPermissions
  if (perms && perms.length > 0 && !perms.every((c) => p.permissions.includes(c))) return false

  return true
}

/**
 * True when the principal satisfies every matched record of a destination —
 * the parent section as well as the leaf, which is how vue-router resolves it.
 */
export function passesRoute(
  p: Principal,
  route: Pick<RouteLocationNormalized, 'matched'>,
): boolean {
  return route.matched.every((r) => passesRecord(p, r as unknown as Gated))
}

/**
 * The first `/school` surface this principal may open, in the order a school
 * would expect to land on one, or null when they may open none.
 *
 * <p>Named routes rather than paths so a path rename cannot silently turn this
 * into a redirect to nowhere.
 */
const LANDING_CANDIDATES: string[] = [
  'school.dashboard',
  'school.canteen.dashboard',
  'school.exams.list',
  'school.attendance.register',
  'school.timetable.grid',
  'school.curriculum.subjects',
  'school.staffing.allocations',
  'school.transport.buses',
  'school.reports.finance',
]

export function landingRouteName(p: Principal, router: Router): string | null {
  for (const name of LANDING_CANDIDATES) {
    const resolved = tryResolve(router, name)
    if (resolved && passesRoute(p, resolved)) return name
  }
  return null
}

function tryResolve(router: Router, name: string): { matched: RouteRecordNormalized[] } | null {
  try {
    return router.resolve({ name })
  } catch {
    // A candidate that no longer exists is a stale entry in the list above,
    // not a reason to strand the user on the sign-in screen.
    return null
  }
}

/**
 * Whether a nav path is worth showing this principal.
 *
 * <p>A path that no longer exists resolves to the catch-all rather than to
 * nothing, so that has to be excluded explicitly — otherwise a mistyped nav
 * entry renders happily and 404s on click.
 */
export function canOpenPath(p: Principal, router: Router, path: string): boolean {
  const resolved = router.resolve(path)
  if (resolved.matched.length === 0 || resolved.name === 'not-found') return false
  return passesRoute(p, resolved)
}
